import { useState, useEffect, useCallback } from 'react';
import { apiClient, ApiClientError } from '../api/client';
import type { User, AuthState } from '../types';

// ==================================================
// 쿠키 헬퍼
// ==================================================

/**
 * 쿠키에서 otj_token을 읽어 인증 여부를 확인합니다.
 * 카페24 OAuth 콜백 후 서버에서 HttpOnly 쿠키로 발급됩니다.
 *
 * 주의: HttpOnly 쿠키는 JS에서 읽을 수 없습니다.
 * 따라서 쿠키 존재 여부 대신 /auth/me API 호출 결과로 인증 여부를 판단합니다.
 */
function clearOtjCookie(): void {
  // 로그아웃 시 쿠키 만료 처리 (서버에서 발급한 HttpOnly 쿠키는 직접 삭제 불가)
  // 서버 /auth/logout 엔드포인트에서 Set-Cookie: otj_token=; Max-Age=0 처리 필요
  document.cookie = 'otj_token=; Path=/; Max-Age=0; SameSite=Lax';
}

// ==================================================
// 상태 초기화
// ==================================================

function getInitialState(): AuthState {
  return {
    user: null,
    isAuthenticated: false,
    isLoading: true, // 항상 /auth/me 검증 필요 (쿠키 기반이므로 JS에서 직접 확인 불가)
  };
}

// ==================================================
// 훅
// ==================================================

export function useAuth() {
  const [state, setState] = useState<AuthState>(getInitialState);

  // 앱 시작 시 /auth/me로 사용자 정보 복원
  // 쿠키는 HttpOnly이므로 JS에서 읽을 수 없고, 브라우저가 자동으로 전송합니다
  useEffect(() => {
    apiClient
      .get<User>('/auth/me')
      .then((user) => {
        setState({ user, isAuthenticated: true, isLoading: false });
      })
      .catch(() => {
        setState({ user: null, isAuthenticated: false, isLoading: false });
      });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (err) {
      // 서버 오류여도 로컬 상태는 초기화
      if (!(err instanceof ApiClientError)) throw err;
    } finally {
      clearOtjCookie();
      setState({ user: null, isAuthenticated: false, isLoading: false });
    }
  }, []);

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    logout,
  };
}
